/**
 * Mirrors packages/amgdragontiger_v2/lib/domain/entities/bet_collection.dart
 * (read the source if porting deeper logic; we only need the shape used by SP).
 */
import type { BetType } from '../types/game';
import { BET_ITEM_ID } from '../types/game';

export interface BetEntry {
  bet: BetType;
  amount: number;
  chips: number[]; // history of chip denominations placed (for animations/undo)
}

export class BetCollection {
  private entries: Map<BetType, BetEntry> = new Map();

  static empty(): BetCollection {
    return new BetCollection();
  }

  copy(): BetCollection {
    const c = new BetCollection();
    for (const [k, v] of this.entries) {
      c.entries.set(k, { bet: v.bet, amount: v.amount, chips: [...v.chips] });
    }
    return c;
  }

  add(bet: BetType, amount: number): void {
    const cur = this.entries.get(bet);
    if (cur) {
      cur.amount += amount;
      cur.chips.push(amount);
    } else {
      this.entries.set(bet, { bet, amount, chips: [amount] });
    }
  }

  clear(): void {
    this.entries.clear();
  }

  clearBet(bet: BetType): void {
    this.entries.delete(bet);
  }

  amountFor(bet: BetType): number {
    return this.entries.get(bet)?.amount ?? 0;
  }

  chipsFor(bet: BetType): readonly number[] {
    return this.entries.get(bet)?.chips ?? [];
  }

  get total(): number {
    let n = 0;
    for (const v of this.entries.values()) n += v.amount;
    return n;
  }

  get isEmpty(): boolean {
    return this.entries.size === 0;
  }

  get bets(): readonly BetType[] {
    return Array.from(this.entries.keys());
  }

  /**
   * Convert into the body lines expected by /action/bet.
   * Mirrors `BetCollection.toApiFormat()` in
   *   packages/amgdragontiger_v2/lib/domain/entities/bet.dart:59-67
   * — entries with amount > 0 only, field name is `amount`.
   */
  toBetLines(): { betItemId: number; amount: number }[] {
    const out: { betItemId: number; amount: number }[] = [];
    for (const v of this.entries.values()) {
      if (v.amount <= 0) continue;
      out.push({ betItemId: BET_ITEM_ID[v.bet], amount: v.amount });
    }
    return out;
  }

  /**
   * Like {@link toBetLines}, but emits ONE line per individual chip placed
   * instead of one line carrying the per-area total.
   *
   * Multiplayer bets go through the SignalR hub (`hub.placeBet`), which
   * validates each submitted `amount` against the configured chip
   * denominations and rejects anything that isn't a valid chip value with
   * "Invalid Chip Amount". A summed per-area total (e.g. 100+100+100 = 300)
   * is not itself a denomination, so the batched total from toBetLines() is
   * rejected. Submitting each chip separately keeps every amount a valid
   * denomination. (Single-player's REST endpoint accepts the total, so it
   * keeps using toBetLines.)
   *
   * Uses the IDENTICAL betItemId derivation and skip conditions as
   * toBetLines(); the only difference is iterating `v.chips`. Invariant: for
   * each betItemId, the sum of toChipLines amounts == the toBetLines amount.
   */
  toChipLines(): { betItemId: number; amount: number }[] {
    const out: { betItemId: number; amount: number }[] = [];
    for (const v of this.entries.values()) {
      if (v.amount <= 0) continue;
      for (const chip of v.chips) {
        if (chip <= 0) continue;
        out.push({ betItemId: BET_ITEM_ID[v.bet], amount: chip });
      }
    }
    return out;
  }
}
